import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";

import { Drawer } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import ChevronRight from "../../../../../components/Generic/Icons/ChevronRight";
import Close from "../../../../../components/Generic/Icons/Close";
import { useCustomerPortalContext } from "../../../../../context/CustomerPortalContext";
import { CustomerPortalMilestoneStep } from "../../../../../models/customer.portal";
import {
  CustomerPortalMilestoneStepStatus,
  MilestonesDefaultTasksTitles,
  MilestonesStep,
} from "../../../../../util/enums";
import { StepAction } from "./components/StepAction";
import useStyles, {
  ComponentItem,
  ComponentSection,
  Container,
  Content,
  MileStonesDesktopHeader,
  MileStonesDesktopTitle,
  MileStonesIconButton,
  MileStonesMobileHeader,
  MileStonesMobileIconButton,
  MileStonesMobileTitle,
} from "./styles";

const getActionByStatus = (status?: string): CustomerPortalMilestoneStepStatus => {
  switch (status) {
    case CustomerPortalMilestoneStepStatus.PENDING:
      return CustomerPortalMilestoneStepStatus.PENDING;
    case CustomerPortalMilestoneStepStatus.IN_PROGRESS:
      return CustomerPortalMilestoneStepStatus.IN_PROGRESS;
    case CustomerPortalMilestoneStepStatus.COMPLETED:
      return CustomerPortalMilestoneStepStatus.COMPLETED;
    default:
      return CustomerPortalMilestoneStepStatus.PENDING;
  }
};

export type CustomerPortalMilestonesHandles = {
  open: () => void;
};

type CustomerPortalMilestonesProps = {
  isMobileDevice: boolean;
  onOpenShipment: (shipmentId: number) => void;
  milestonesSteps?: CustomerPortalMilestoneStep[];
};

const CustomerPortalMilestonesComponent: React.ForwardRefRenderFunction<
  CustomerPortalMilestonesHandles,
  CustomerPortalMilestonesProps
> = (props, ref) => {
  const { milestonesSteps, isMobileDevice, onOpenShipment } = props;
  const { isMileStonesOpenedInMobile, handleCloseMileStonesInMobile } = useCustomerPortalContext();

  const lastMilestonesStep = milestonesSteps?.[Number(milestonesSteps?.length) - 1];

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const [isOpened, setIsOpened] = useState(false);

  const onOpen = useCallback(() => setIsOpened(true), []);

  const onClose = () => {
    setIsOpened(false);
  };

  const groupByAction = (
    milestones: CustomerPortalMilestoneStep[]
  ): CustomerPortalMilestoneStep[] => {
    const milestonesWithDate: CustomerPortalMilestoneStep[] = [];
    const milestonesWithoutDate: CustomerPortalMilestoneStep[] = [];
    const deliveryMilestones: CustomerPortalMilestoneStep[] = [];

    const milestonesToIterate = _.cloneDeep(milestones);

    milestonesToIterate.forEach((milestone) => {
      if (milestone.action === MilestonesStep.delivery) {
        deliveryMilestones.push(milestone);
      } else if (milestone.actionDate) {
        milestonesWithDate.push(milestone);
      } else {
        milestonesWithoutDate.push(milestone);
      }
    });

    milestonesWithDate.sort((a, b) => {
      const dateA = new Date(a.actionDate || "");
      const dateB = new Date(b.actionDate || "");

      return dateA.getTime() - dateB.getTime();
    });

    milestonesWithoutDate.sort((a, b) => a.sequence - b.sequence);

    deliveryMilestones.sort((a, b) => a.sequence - b.sequence);

    const withDateGrouped: { [key: string]: CustomerPortalMilestoneStep[] } = {};
    const withoutDateGrouped: { [key: string]: CustomerPortalMilestoneStep[] } = {};
    const deliveryGrouped: { [key: string]: CustomerPortalMilestoneStep[] } = {};

    milestonesWithDate.forEach((milestone) => {
      if (!withDateGrouped[milestone.action]) {
        withDateGrouped[milestone.action] = [];
      }
      withDateGrouped[milestone.action].push(milestone);
    });

    milestonesWithoutDate.forEach((milestone) => {
      if (!withoutDateGrouped[milestone.action]) {
        withoutDateGrouped[milestone.action] = [];
      }
      withoutDateGrouped[milestone.action].push(milestone);
    });

    deliveryMilestones.forEach((milestone) => {
      if (!deliveryGrouped[milestone.action]) {
        deliveryGrouped[milestone.action] = [];
      }
      deliveryGrouped[milestone.action].push(milestone);
    });

    Object.values(withDateGrouped).forEach((group) => {
      if (group.length > 1) {
        group.forEach((milestone, index) => {
          milestone.action = `${milestone.action} (${index + 1})`;
        });
      }
    });

    Object.values(withoutDateGrouped).forEach((group) => {
      if (group.length > 1) {
        group.forEach((milestone, index) => {
          milestone.action = `${milestone.action} (${index + 1})`;
        });
      }
    });

    Object.values(deliveryGrouped).forEach((group) => {
      if (group.length > 1) {
        group.forEach((milestone, index) => {
          milestone.action = `${milestone.action} (${index + 1})`;
        });
      }
    });

    const flattenedMilestones: CustomerPortalMilestoneStep[] = [];

    Object.values(withDateGrouped).forEach((group) => {
      flattenedMilestones.push(...group);
    });

    Object.values(withoutDateGrouped).forEach((group) => {
      flattenedMilestones.push(...group);
    });

    Object.values(deliveryGrouped).forEach((group) => {
      flattenedMilestones.push(...group);
    });

    return flattenedMilestones;
  };

  const milestoneSorted = useMemo(() => {
    const sortedMilestones = _.cloneDeep(milestonesSteps || []);

    return sortedMilestones.sort((a, b) => a.sequence - b.sequence);
  }, [milestonesSteps]);

  const renderSection = () => {
    return (
      <Container>
        <Content>
          {milestoneSorted?.map((step, index) => {
            if (
              step?.status !== CustomerPortalMilestoneStepStatus.NA &&
              Object.values(MilestonesDefaultTasksTitles).includes(
                step.action as MilestonesDefaultTasksTitles
              )
            ) {
              return (
                <StepAction
                  stepStatus={getActionByStatus(step?.status)}
                  title={step?.action}
                  actionDate={step?.actionDate}
                  hideLine={lastMilestonesStep?.sequence === step?.sequence}
                  subSteps={groupByAction(step.steps || [])}
                  key={`${step.action} ${index}`}
                  closeMilestoneDrawer={isMobileDevice ? handleCloseMileStonesInMobile : onClose}
                  onOpenShipment={onOpenShipment}
                />
              );
            }

            return <React.Fragment key={index} />;
          })}
        </Content>
      </Container>
    );
  };

  const DesktopHeader = () => {
    return (
      <MileStonesDesktopHeader>
        <MileStonesDesktopTitle data-cy="sidebarTitle">
          {translate("milestones")}
        </MileStonesDesktopTitle>
        <MileStonesIconButton
          data-cy="iconButtonClose"
          onClick={isMobileDevice ? handleCloseMileStonesInMobile : onClose}
        >
          <Close width={24} height={24} color={colors.gray600} />
        </MileStonesIconButton>
      </MileStonesDesktopHeader>
    );
  };

  const MobileHeader = () => {
    return (
      <MileStonesMobileHeader>
        <MileStonesMobileIconButton
          data-cy="iconButtonClose"
          onClick={isMobileDevice ? handleCloseMileStonesInMobile : onClose}
        >
          <ChevronRight height={24} width={24} color={colors.gray600} />
        </MileStonesMobileIconButton>
        <MileStonesMobileTitle data-cy="sidebarTitle">
          {translate("milestones")}
        </MileStonesMobileTitle>
      </MileStonesMobileHeader>
    );
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
  }));

  return (
    <Drawer
      anchor="right"
      open={isMobileDevice ? isMileStonesOpenedInMobile : isOpened}
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: false,
      }}
      onClose={isMobileDevice ? handleCloseMileStonesInMobile : onClose}
      className={classes.wrapper}
    >
      {isMobileDevice ? <MobileHeader /> : <DesktopHeader />}
      <ComponentSection id="componentSection">
        <ComponentItem>{renderSection()}</ComponentItem>
      </ComponentSection>
    </Drawer>
  );
};

export const CustomerPortalMilestones = forwardRef(CustomerPortalMilestonesComponent);
